/**
 * Rep prescriptions in the program come in several shapes:
 *
 *   "10-12"        a range      -> start at the minimum, 10
 *   "8"            a fixed count
 *   "4, 6, 8"      per-set      -> set 1 does 4, set 2 does 6, set 3 does 8
 *   "5,4,3+"       per-set, descending
 *   "8-10 + 4-6"   a main range plus a technique -> take the main minimum
 *   "AMRAP"        no number at all
 *
 * `startingReps` returns the value a set should start at, so the +/- stepper
 * always begins on the minimum recommendation rather than empty.
 */
export function startingReps(
  prescription: string | null | undefined,
  workingIndex: number,
): number | null {
  if (!prescription) return null;
  const text = prescription.trim();
  if (!text) return null;

  // A comma-separated list is per-set, not a range: "4, 6, 8".
  if (text.includes(',')) {
    const parts = text.split(',').map(p => p.trim()).filter(Boolean);
    // Clamp so extra sets reuse the last prescribed value.
    const part = parts[Math.min(workingIndex, parts.length - 1)];
    return firstNumber(part);
  }

  // Otherwise the first number is the minimum of the range (or the exact count).
  return firstNumber(text);
}

function firstNumber(text: string): number | null {
  const m = text.match(/\d+/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Starting reps for a warm-up set, taken from the warm-up pyramid's own rep
 * guidance ("6-10", "4-6", "3-4") rather than the exercise's working range.
 */
export function startingWarmupReps(rungReps: string | null | undefined): number | null {
  if (!rungReps) return null;
  return firstNumber(rungReps);
}
